import { Controller } from "@hotwired/stimulus"
import { queueOutboxMessage } from "../chat_offline_resources"

export default class extends Controller {
  static targets = [
    "messages", "input", "form", "submitButton", "search", "message",
    "replyPreview", "replySender", "replySnippet", "replyInput",
    "pendingInput",
    "unreadPill", "unreadPillLabel",
    "emojiOverlay", "emojiPicker"
  ]

  connect() {
    this.scrollToBottom()
    this.observeNewMessages()
    this._updateSubmitButton()
    this._setupViewportListener()
    this._checkUnread()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this._teardownViewportListener()
  }

  handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      if (this.hasInputTarget && this.inputTarget.value.trim() !== "") {
        this.formTarget.requestSubmit()
      }
    }
  }

  // Intercept form submit: if offline, queue to outbox instead.
  // If online, inject a pending_id and optimistically render the bubble.
  async handleSubmit(event) {
    const isOffline = localStorage.getItem("chat:online") === "false" || !navigator.onLine

    if (isOffline) {
      event.preventDefault()
      event.stopPropagation()

      const content = this.hasInputTarget ? this.inputTarget.value.trim() : ""
      if (!content) return

      const action = this.formTarget.action || ""
      const match  = action.match(/\/chats\/(\d+)\//)
      const chatId = match ? parseInt(match[1]) : null
      if (!chatId) return

      try {
        await queueOutboxMessage(chatId, content)
        this._renderPendingBubble(content)
        this._resetInput()
        this.scrollToBottom()
      } catch (err) {
        console.error("[Chat] Failed to queue outbox message:", err)
      }
      return
    }

    // Online: optimistic rendering — inject a pending_id before Turbo submits
    const content = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    const fileInput = this.formTarget.querySelector("input[type='file']")
    const hasFiles  = fileInput && fileInput.files && fileInput.files.length > 0
    if (!content && !hasFiles) return

    const pendingId = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2))
    if (this.hasPendingInputTarget) this.pendingInputTarget.value = pendingId
    if (content) this._renderPendingBubble(content, pendingId)
    // Let Turbo handle the actual POST (do not preventDefault)
  }

  onSubmitEnd() {
    this._resetInput()
    this.clearReply()
    this.scrollToBottom()
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  autoResize(event) {
    const textarea = event.target
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px"
    this._updateSubmitButton()
  }

  filterMessages() {
    if (!this.hasSearchTarget) return
    const query = this.searchTarget.value.toLowerCase().trim()
    this.messageTargets.forEach(msg => {
      const text = msg.textContent.toLowerCase()
      msg.classList.toggle("hidden", query !== "" && !text.includes(query))
    })
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      requestAnimationFrame(() => {
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      })
    }
  }

  observeNewMessages() {
    if (!this.hasMessagesTarget) return
    this.observer = new MutationObserver(() => { this.scrollToBottom() })
    this.observer.observe(this.messagesTarget, { childList: true, subtree: true })
  }

  // Reply handling
  setReply(event) {
    const btn = event.currentTarget
    const messageId = btn.dataset.messageId
    const sender    = btn.dataset.messageSender
    const snippet   = btn.dataset.messageSnippet

    if (this.hasReplyInputTarget)   this.replyInputTarget.value   = messageId
    if (this.hasReplySenderTarget)  this.replySenderTarget.textContent = sender
    if (this.hasReplySnippetTarget) this.replySnippetTarget.textContent = snippet
    if (this.hasReplyPreviewTarget) this.replyPreviewTarget.classList.remove("hidden")
    if (this.hasInputTarget)        this.inputTarget.focus()
  }

  clearReply() {
    if (this.hasReplyInputTarget)   this.replyInputTarget.value   = ""
    if (this.hasReplySenderTarget)  this.replySenderTarget.textContent = ""
    if (this.hasReplySnippetTarget) this.replySnippetTarget.textContent = ""
    if (this.hasReplyPreviewTarget) this.replyPreviewTarget.classList.add("hidden")
  }

  // Called by chat-attachments controller when files change
  checkSendable() {
    this._updateSubmitButton()
  }

  // Emoji picker
  showEmojiPicker(event) {
    const btn = event.currentTarget
    this._pendingReactMessageId = btn.dataset.messageId
    this._pendingReactChatId    = btn.dataset.chatId

    if (!this.hasEmojiOverlayTarget || !this.hasEmojiPickerTarget) return

    // Position picker near the button
    const rect = btn.getBoundingClientRect()
    const picker = this.emojiPickerTarget
    picker.style.top  = (rect.top - 56) + "px"
    picker.style.left = Math.min(rect.left, window.innerWidth - 220) + "px"

    this.emojiOverlayTarget.classList.remove("hidden")
  }

  hideEmojiPicker() {
    if (this.hasEmojiOverlayTarget) this.emojiOverlayTarget.classList.add("hidden")
    this._pendingReactMessageId = null
    this._pendingReactChatId    = null
  }

  pickEmoji(event) {
    const emoji   = event.currentTarget.dataset.emoji
    const msgId   = this._pendingReactMessageId
    const chatId  = this._pendingReactChatId
    this.hideEmojiPicker()

    if (!emoji || !msgId || !chatId) return

    fetch(`/chats/${chatId}/chat_messages/${msgId}/react`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ emoji })
    }).then(res => {
      if (res.ok) return res.text()
    }).then(html => {
      if (!html) return
      const target = document.getElementById(`reactions_for_${msgId}`)
      if (target) {
        target.outerHTML = html
      }
    }).catch(err => console.error("[Chat] React error:", err))
  }

  // Unread jump
  jumpToUnread() {
    const lastReadAt = parseInt(this.element.dataset.chatLastReadAt || "0", 10)
    const messages = this.element.querySelectorAll("[data-created-at]")
    let firstUnread = null

    for (const msg of messages) {
      const createdAt = parseInt(msg.dataset.createdAt || "0", 10)
      if (createdAt > lastReadAt) {
        firstUnread = msg
        break
      }
    }

    if (firstUnread && this.hasMessagesTarget) {
      firstUnread.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    this._hideUnreadPill()
  }

  // -- private --

  _checkUnread() {
    const lastReadAt = parseInt(this.element.dataset.chatLastReadAt || "0", 10)
    if (lastReadAt === 0) return

    const messages = this.element.querySelectorAll("[data-created-at]")
    let count = 0
    for (const msg of messages) {
      if (parseInt(msg.dataset.createdAt || "0", 10) > lastReadAt) count++
    }

    if (count > 0) {
      this._showUnreadPill(count)
    }
  }

  _showUnreadPill(count) {
    if (!this.hasUnreadPillTarget) return
    if (this.hasUnreadPillLabelTarget) {
      this.unreadPillLabelTarget.textContent = count === 1 ? "1 new message" : `${count} new messages`
    }
    this.unreadPillTarget.classList.remove("hidden")

    // Auto-hide after 8 seconds
    clearTimeout(this._unreadPillTimer)
    this._unreadPillTimer = setTimeout(() => this._hideUnreadPill(), 8000)

    // Hide when user scrolls near the bottom
    if (this.hasMessagesTarget) {
      const onScroll = () => {
        const el = this.messagesTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
          this._hideUnreadPill()
          el.removeEventListener("scroll", onScroll)
        }
      }
      this.messagesTarget.addEventListener("scroll", onScroll)
    }
  }

  _hideUnreadPill() {
    if (this.hasUnreadPillTarget) this.unreadPillTarget.classList.add("hidden")
  }

  _updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return
    const hasText  = this.hasInputTarget && this.inputTarget.value.trim().length > 0
    const fileInput = this.element.querySelector("input[type='file']")
    const hasFiles  = fileInput && fileInput.files && fileInput.files.length > 0
    this.submitButtonTarget.disabled = !hasText && !hasFiles
  }

  _resetInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.style.height = "auto"
    }
    if (this.hasPendingInputTarget) this.pendingInputTarget.value = ""
    this._updateSubmitButton()
  }

  _renderPendingBubble(content, pendingId = null) {
    const container = this.hasMessagesTarget
      ? this.messagesTarget.querySelector("#chat_messages")
      : null
    if (!container) return

    const time = new Date().toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })
    const esc  = str => String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const id   = pendingId ? `id="pending-${esc(pendingId)}"` : ""

    const bubble = document.createElement("div")
    bubble.className = "flex justify-end mb-2"
    if (pendingId) bubble.id = `pending-${pendingId}`
    bubble.innerHTML = `
      <div class="max-w-[80%]">
        <div class="px-3.5 py-2.5 text-sm break-words leading-relaxed bg-sapphire/60 text-white rounded-2xl rounded-br-sm">
          <span class="whitespace-pre-wrap">${esc(content)}</span>
        </div>
        <div class="flex items-center justify-end gap-1 mt-1 px-1">
          <p class="text-xs opacity-60 text-slate-400">${esc(time)}</p>
          <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 text-slate-300 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
    `
    container.appendChild(bubble)
  }

  // Adjust fixed container bottom when virtual keyboard appears
  _setupViewportListener() {
    if (!window.visualViewport) return
    this._viewportHandler = () => {
      const offset = window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop
      this.element.style.bottom = Math.max(0, offset) + "px"
    }
    window.visualViewport.addEventListener("resize", this._viewportHandler)
    window.visualViewport.addEventListener("scroll", this._viewportHandler)
  }

  _teardownViewportListener() {
    if (!window.visualViewport || !this._viewportHandler) return
    window.visualViewport.removeEventListener("resize", this._viewportHandler)
    window.visualViewport.removeEventListener("scroll", this._viewportHandler)
  }
}
